import React, { useLayoutEffect, useRef, useState } from "react";
import { measureTextClass, truncatedPathContainerClass } from "./styles";

type Props = {
  segments: string[];
  className: string;
  ellipsisPrefix?: string;
  onTruncatedChange?: (isTruncated: boolean) => void;
};

const TruncatedFolderPath: React.FC<Props> = ({ segments, className, ellipsisPrefix = "...", onTruncatedChange }) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [displaySegmentCount, setDisplaySegmentCount] = useState(segments.length);
  // True when any content is hidden: either whole segments were dropped, or the
  // remaining single segment is itself wider than the box (CSS-clipped).
  const [isOverflowing, setIsOverflowing] = useState(false);

  const fullPath = segments.join("/");
  const lastWidthRef = useRef(-1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const pathSegments = fullPath ? fullPath.split("/") : [];

    const widthOf = (text: string) => {
      measure.textContent = text;
      return measure.getBoundingClientRect().width;
    };

    const computeCount = (availableWidth: number) => {
      if (pathSegments.length === 0 || widthOf(fullPath) <= availableWidth) return pathSegments.length;

      let kept = 1;
      for (let count = 2; count <= pathSegments.length; count++) {
        const candidate = `${ellipsisPrefix}${pathSegments.slice(pathSegments.length - count).join("/")}`;
        if (widthOf(candidate) > availableWidth) break;
        kept = count;
      }
      return kept;
    };

    const recalculate = (availableWidth: number) => {
      const nextCount = computeCount(availableWidth);
      const displayed =
        nextCount < pathSegments.length
          ? `${ellipsisPrefix}${pathSegments.slice(pathSegments.length - nextCount).join("/")}`
          : pathSegments.slice(pathSegments.length - nextCount).join("/");
      const nextOverflowing = nextCount < pathSegments.length || widthOf(displayed) > availableWidth;
      // Functional updates so React bails out when unchanged, which prevents any
      // ResizeObserver feedback (text change -> resize) from looping.
      setDisplaySegmentCount((prev) => (prev === nextCount ? prev : nextCount));
      setIsOverflowing((prev) => (prev === nextOverflowing ? prev : nextOverflowing));
    };

    lastWidthRef.current = container.clientWidth;
    recalculate(lastWidthRef.current);

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.clientWidth;
      if (Math.abs(width - lastWidthRef.current) < 1) return;
      lastWidthRef.current = width;
      recalculate(width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [fullPath, ellipsisPrefix]);

  const isTruncated = displaySegmentCount < segments.length || isOverflowing;

  const onTruncatedChangeRef = useRef(onTruncatedChange);
  onTruncatedChangeRef.current = onTruncatedChange;

  useLayoutEffect(() => {
    onTruncatedChangeRef.current?.(isTruncated);
  }, [isTruncated]);

  const segmentsDropped = displaySegmentCount < segments.length;
  const kept = segments.slice(segments.length - displaySegmentCount);
  const displayText = segmentsDropped ? `${ellipsisPrefix}${kept.join("/")}` : kept.join("/");

  return (
    <span ref={containerRef} className={`${truncatedPathContainerClass} ${className}`}>
      <span ref={measureRef} className={measureTextClass} aria-hidden="true" />
      {displayText}
    </span>
  );
};

export default TruncatedFolderPath;
